// ============================================================================
//  ConditionEditor.js — create/edit a condition template.
// ----------------------------------------------------------------------------
//  Wide two-panel modal:
//
//    LEFT · SOURCE PICKER
//      • Diagnosis / Procedure toggle
//      • Debounced search over the imported NHIS booklet
//      • Expandable rows — a diagnosis expands to reveal its G-DRG variants
//        (e.g. B54 Malaria → MEDI28 "Malaria ≥12 Yrs" + PAED36 "Malaria <12 Yrs")
//      • Procedure rows expand to reveal sub-procedures + attached diagnoses
//      • Picking a G-DRG variant anchors the template on it (sourceType='procedure')
//
//    RIGHT · SELECTION PREVIEW + FORM
//      • Big card showing the picked source with codes / MDC / attached items
//      • Care setting + age scope (auto-suggested from the pick, overridable)
//      • Category + notes
//
//    EDIT MODE
//      • The source binding is locked — you can only edit meta.
//      • The left panel is hidden.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  templateConditions, templateCondition,
  templateSources, templateAttachedDiagnoses,
  templateSubProcedures, templateDiagnosisGdrgs,
} from '../../BaseURL';
import notify from './notify';
import { IconSearch, IconCheck, IconChevron, IconClose } from './icons';

const CATEGORIES = [
  'infectious', 'cardiovascular', 'respiratory', 'obgyn',
  'paediatric', 'trauma', 'chronic', 'other',
];

// Paediatric MDCs from the NHIS booklet — used to auto-suggest age scope.
const PAED_MDCS       = new Set(['PAED', 'PSUR']);
// Outpatient-flavoured MDCs — everything else defaults to inpatient.
const OUTPATIENT_MDCS = new Set(['OPDC', 'INVE']);

// ── Auto-suggest helpers ──────────────────────────────────────────────
function suggestAgeForDiagnosis(icd) {
  if (!icd) return 'both';
  const first = String(icd).charAt(0).toUpperCase();
  if (first === 'P' || first === 'Q') return 'paediatric';
  return 'both';
}

function suggestAgeForGdrg(row) {
  if (!row) return 'both';
  const name = String(row.name || '');
  if (/(≤|<=?|under\s+)\s*12/i.test(name)) return 'paediatric';
  if (/(≥|>=?|over\s+)\s*12/i.test(name))  return 'adult';
  if (PAED_MDCS.has(row.mdc_code))         return 'paediatric';
  const variants = String(row.variant_ages || '').split(',').filter(Boolean);
  if (variants.length === 1 && variants[0] === 'paediatric') return 'paediatric';
  if (variants.length === 1 && variants[0] === 'adult')      return 'adult';
  return 'both';
}

function suggestCareForGdrg(row) {
  if (!row) return 'both';
  if (OUTPATIENT_MDCS.has(row.mdc_code)) return 'outpatient';
  // Everything else in kind='service' is inpatient management by convention.
  return 'inpatient';
}

// ────────────────────────────────────────────────────────────────────────
export default function ConditionEditor({ initial, defaultCareSetting, onClose, onSaved }) {
  const isEdit = !!initial?.id;

  // Source pick — for a fresh template it's `{ type, ref }` where ref is the
  // full row we picked (either a diagnosis row or a G-DRG row).
  const [sourceType, setSourceType] = useState('diagnosis');
  const [picked, setPicked] = useState(null); // { kind: 'diagnosis'|'procedure', row }

  // For an edit, the row's saved care_setting wins. For a fresh template the
  // parent Templates.js passes the active sub-tab's setting (outpatient /
  // inpatient) so the user doesn't have to re-pick it.
  const [careSetting, setCareSetting] = useState(
    initial?.care_setting || defaultCareSetting || 'both'
  );
  const [ageScope,    setAgeScope]    = useState(initial?.age_scope    || 'both');
  const [category,    setCategory]    = useState(initial?.category     || 'infectious');
  const [notes,       setNotes]       = useState(initial?.notes        || '');
  const [saving,      setSaving]      = useState(false);

  const onPick = (kind, row) => {
    setPicked({ kind, row });
    if (kind === 'procedure') {
      setAgeScope(suggestAgeForGdrg(row));
      setCareSetting(suggestCareForGdrg(row));
    } else {
      setAgeScope(suggestAgeForDiagnosis(row.code));
      setCareSetting('both');
    }
  };

  const canSave = useMemo(() => {
    if (isEdit) return true;
    return !!picked?.row?.id;
  }, [isEdit, picked]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(templateCondition(initial.id), {
          category, ageScope, careSetting, notes,
        });
      } else {
        await axios.post(templateConditions, {
          // Technical anchor — tells the backend which table to hydrate from.
          // Drilling into a G-DRG variant from the Diagnosis tab gives us
          // picked.kind='procedure' here, which is correct for hydration.
          sourceType:  picked.kind,
          sourceRefId: picked.row.id,
          // Display origin — which tab the user actually picked from. So a
          // diagnosis drill-down still shows a "Diagnosis" pill even though
          // the anchor is a G-DRG.
          pickOrigin:  sourceType,
          category, ageScope, careSetting, notes,
        });
      }
      notify.toast.success(isEdit ? 'Template updated' : 'Template created');
      onSaved?.();
    } catch (err) {
      notify.error('Save failed', err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tp-modal-scrim" onClick={onClose}>
      <div className={`tp-modal ${isEdit ? 'tp-modal-wide' : 'tp-modal-huge'}`}
           onClick={(e) => e.stopPropagation()}>
        <div className="tp-modal-head-bar">
          <div>
            <h3>{isEdit ? 'Edit template' : 'New template'}</h3>
            <p className="tp-modal-sub">
              {isEdit
                ? 'Source binding is locked — edit meta only. To change the source, delete and re-create.'
                : 'Pick a diagnosis or procedure from the NHIS booklet. Diagnosis rows expand to reveal the specific G-DRG variants that treat them — that\'s what most templates should anchor on.'}
            </p>
          </div>
          <button type="button" className="tp-icon-btn" onClick={onClose} title="Close">
            <IconClose size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="tp-editor-body">
          {isEdit ? (
            <SelectionPanel
              picked={{
                kind: initial.source_type === 'manual' ? 'diagnosis' : initial.source_type,
                row: {
                  id: initial.source_ref_id,
                  code: initial.source_code,
                  name: initial.name,
                  mdc_code: null,
                  mdc_name: null,
                },
              }}
              locked
              careSetting={careSetting} setCareSetting={setCareSetting}
              ageScope={ageScope}       setAgeScope={setAgeScope}
              category={category}       setCategory={setCategory}
              notes={notes}             setNotes={setNotes}
            />
          ) : (
            <div className="tp-editor-cols">
              <SourcePicker
                sourceType={sourceType}
                onChangeType={(t) => { setSourceType(t); setPicked(null); }}
                picked={picked}
                onPick={onPick}
              />
              <SelectionPanel
                picked={picked}
                careSetting={careSetting} setCareSetting={setCareSetting}
                ageScope={ageScope}       setAgeScope={setAgeScope}
                category={category}       setCategory={setCategory}
                notes={notes}             setNotes={setNotes}
              />
            </div>
          )}

          <div className="tp-modal-actions">
            <button type="button" className="tp-btn tp-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="tp-btn tp-btn-primary" disabled={saving || !canSave}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── LEFT · Source picker with expandable rows ──────────────────────────── */
function SourcePicker({ sourceType, onChangeType, picked, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(templateSources, {
          params: { type: sourceType, q },
        });
        setRows(data.rows || []);
        setExpandedId(null);
      } catch (err) {
        notify.toast.error('Source search failed');
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [sourceType, q]);

  const isPickedRow = (kind, id) => picked?.kind === kind && picked?.row?.id === id;

  return (
    <div className="tp-source-panel">
      <div className="tp-source-toggle" role="tablist">
        {['diagnosis', 'procedure'].map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={sourceType === t}
            className={`tp-source-tab ${sourceType === t ? 'is-on' : ''}`}
            onClick={() => onChangeType(t)}
          >
            {t === 'diagnosis' ? 'Diagnosis (ICD-10)' : 'Procedure (G-DRG)'}
          </button>
        ))}
      </div>

      <div className="tp-source-search">
        <IconSearch size={14} />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={sourceType === 'diagnosis'
            ? 'Search ICD-10 code or diagnosis name…'
            : 'Search G-DRG code or procedure name…'}
          autoFocus
        />
      </div>

      <div className="tp-source-list tp-source-list-tall">
        {loading && <div className="tp-source-empty">Searching…</div>}
        {!loading && rows.length === 0 && (
          <div className="tp-source-empty">
            No matches. {q ? 'Try a broader term.' : 'Type to search.'}
          </div>
        )}
        {!loading && rows.map((r) => {
          const isExpanded = expandedId === r.id;
          // Only diagnoses drill down — a diagnosis has to be resolved to a
          // specific G-DRG variant to be a useful template anchor. Procedures
          // ARE the anchor: click a procedure = pick it. Its attached
          // diagnoses show in the right selection panel, informational only,
          // and get used later when documentation tabs let auditors tick
          // which related diagnosis this template call applies to.
          const canExpand = sourceType === 'diagnosis' && r.attached_count > 0;
          return (
            <div key={r.id} className="tp-source-item">
              <button
                type="button"
                className={`tp-source-row ${isPickedRow(sourceType, r.id) ? 'is-picked' : ''}`}
                onClick={() => {
                  if (canExpand) setExpandedId(isExpanded ? null : r.id);
                  else onPick(sourceType, r);
                }}
              >
                <span className="tp-source-code">{r.code}</span>
                <span className="tp-source-name">{r.name}</span>
                <span className="tp-source-tags">
                  {r.mdc_code && <span className="tp-chip">{r.mdc_code}</span>}
                  {r.attached_count > 0 && (
                    <span className="tp-chip tp-chip-muted">
                      {sourceType === 'diagnosis'
                        ? `${r.attached_count} variant${r.attached_count === 1 ? '' : 's'}`
                        : `${r.attached_count} dx`}
                    </span>
                  )}
                </span>
                {canExpand ? (
                  <span className={`tp-chevron ${isExpanded ? 'is-open' : ''}`}>
                    <IconChevron size={14} />
                  </span>
                ) : (
                  <span className="tp-source-choose">Pick</span>
                )}
              </button>

              {isExpanded && (
                <ExpandedChildren
                  parent={r}
                  parentKind={sourceType}
                  picked={picked}
                  onPick={onPick}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Renders the drill-down under an expanded row */
function ExpandedChildren({ parent, parentKind, picked, onPick }) {
  const [children, setChildren] = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const url = parentKind === 'diagnosis'
      ? templateDiagnosisGdrgs(parent.id)
      : templateAttachedDiagnoses(parent.id);
    axios.get(url)
      .then((r) => {
        if (!alive) return;
        setChildren(parentKind === 'diagnosis' ? (r.data.rows || []) : (r.data.diagnoses || []));
      })
      .catch(() => { if (alive) setChildren([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [parent.id, parentKind]);

  if (loading) return <div className="tp-source-children-loading">Loading…</div>;
  if (!children || children.length === 0) return null;

  if (parentKind === 'diagnosis') {
    return (
      <div className="tp-source-children">
        <div className="tp-source-children-head">Pick a G-DRG variant to anchor the template on:</div>
        {children.map((c) => {
          const isPicked = picked?.kind === 'procedure' && picked?.row?.id === c.id;
          return (
            <button
              key={c.id}
              type="button"
              className={`tp-source-child-row ${isPicked ? 'is-picked' : ''}`}
              onClick={() => onPick('procedure', c)}
            >
              <span className="tp-source-code">{c.code}</span>
              <span className="tp-source-name">{c.name}</span>
              <span className="tp-source-tags">
                <span className="tp-chip">{c.mdc_code}</span>
              </span>
              {isPicked && <IconCheck size={14} />}
            </button>
          );
        })}
      </div>
    );
  }

  // parentKind === 'procedure' — show attached diagnoses (informational).
  return (
    <div className="tp-source-children">
      <div className="tp-source-children-head">Diagnoses this procedure treats in the booklet:</div>
      {children.map((d) => (
        <div key={d.id} className="tp-source-child-row is-info">
          <span className="tp-source-code">{d.icd10_code}</span>
          <span className="tp-source-name">{d.description}</span>
        </div>
      ))}
    </div>
  );
}

/* ── RIGHT · Selection preview + form ─────────────────────────────────── */
function SelectionPanel({
  picked, locked,
  careSetting, setCareSetting,
  ageScope,    setAgeScope,
  category,    setCategory,
  notes,       setNotes,
}) {
  if (!picked) {
    return (
      <div className="tp-selection tp-selection-empty">
        <div className="tp-selection-empty-inner">
          <h4>No source picked yet</h4>
          <p>Search the booklet on the left. Click a diagnosis to see its G-DRG variants — pick a variant to continue.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tp-selection">
      <SelectionCard picked={picked} locked={locked} />

      <div className="tp-field">
        <label>Care setting</label>
        <div className="tp-radio-row">
          {[
            { id: 'inpatient',  label: 'Inpatient' },
            { id: 'outpatient', label: 'Outpatient' },
            { id: 'both',       label: 'Both' },
          ].map((s) => (
            <label key={s.id} className={`tp-radio ${careSetting === s.id ? 'is-on' : ''}`}>
              <input type="radio" name="careSetting" value={s.id}
                checked={careSetting === s.id}
                onChange={() => setCareSetting(s.id)} />
              <span>{s.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="tp-field">
        <label>Age scope</label>
        <div className="tp-radio-row">
          {[
            { id: 'paediatric', label: 'Paediatric (≤12)' },
            { id: 'adult',      label: 'Adult (>12)' },
            { id: 'both',       label: 'Both' },
          ].map((s) => (
            <label key={s.id} className={`tp-radio ${ageScope === s.id ? 'is-on' : ''}`}>
              <input type="radio" name="ageScope" value={s.id}
                checked={ageScope === s.id}
                onChange={() => setAgeScope(s.id)} />
              <span>{s.label}</span>
            </label>
          ))}
        </div>
        {!locked && <span className="tp-hint">Suggested from the source — override if you need to.</span>}
      </div>

      <div className="tp-field">
        <label>Category</label>
        <select className="tp-input" value={category}
          onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="tp-field">
        <label>Notes (optional)</label>
        <textarea className="tp-input" rows={3} value={notes}
          placeholder="Anything auditors should know about this template."
          onChange={(e) => setNotes(e.target.value)} />
      </div>
    </div>
  );
}

/* Selection preview card — shows the picked source + drill-down details */
function SelectionCard({ picked, locked }) {
  const { kind, row } = picked;
  const [subProcedures, setSubProcedures] = useState([]);
  const [attachedDiagnoses, setAttachedDiagnoses] = useState([]);

  const loadProcedureDetails = useCallback(async () => {
    if (kind !== 'procedure' || !row?.id) return;
    try {
      const [subRes, atRes] = await Promise.all([
        axios.get(templateSubProcedures(row.id)),
        axios.get(templateAttachedDiagnoses(row.id)),
      ]);
      setSubProcedures(subRes.data.procedures || []);
      setAttachedDiagnoses(atRes.data.diagnoses || []);
    } catch { /* silent — panel just stays empty */ }
  }, [kind, row?.id]);

  useEffect(() => { loadProcedureDetails(); }, [loadProcedureDetails]);

  return (
    <div className="tp-selection-card">
      <div className="tp-selection-head">
        <div>
          <div className="tp-selection-kind">
            {kind === 'diagnosis' ? 'Diagnosis' : 'G-DRG · Procedure/Service'}
            {locked && <span className="tp-chip tp-chip-muted">locked</span>}
          </div>
          <div className="tp-selection-name">{row.name}</div>
          <div className="tp-selection-meta">
            {row.code && <span className="tp-chip">{row.code}</span>}
            {row.mdc_code && <span className="tp-chip">{row.mdc_code} · {row.mdc_name || ''}</span>}
          </div>
        </div>
      </div>

      {kind === 'procedure' && subProcedures.length > 0 && (
        <div className="tp-selection-block">
          <div className="tp-selection-block-head">Sub-procedures listed under this code</div>
          <ul className="tp-mini-list">
            {subProcedures.map((p) => <li key={p.id}>{p.name}</li>)}
          </ul>
        </div>
      )}

      {kind === 'procedure' && attachedDiagnoses.length > 0 && (
        <div className="tp-selection-block">
          <div className="tp-selection-block-head">Diagnoses this procedure treats in the booklet</div>
          <ul className="tp-mini-list">
            {attachedDiagnoses.map((d) => (
              <li key={d.id}><strong>{d.icd10_code}</strong> · {d.description}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
